/**
 * InsightsFieldSelector
 *
 * Autonomous component for selecting aggregated JTBD insights
 * from completed InsightsAnalysis documents.
 *
 * - Loads completed analyses from Firestore independently
 * - Uses rc-tree for 3-level hierarchy: analysis → category → insight
 * - Supports path formats: @insight[id], @insight[id].category, @insight[id].category[itemId]
 * - Optimizes paths: category-level when all items selected, analysis-level when all categories selected
 * - Optional task filter via taskFilter prop
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Typography, LinearProgress } from '@mui/material';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import type { Key } from 'rc-tree/lib/interface';

import { InsightsFirestoreService } from '@modules/insights-analyzer/services/InsightsFirestoreService';
import type {
  InsightsAnalysisDoc,
  InsightsCategoryPackageDoc,
} from '@modules/insights-analyzer/services/InsightsFirestoreService';

import type { InsightsFieldSelectorProps, InsightsTreeNode, InsightsAnalysisForPicker } from './types';
import { countTotalInsights, insightsToTreeNodes } from './insightsToTreeNode';
import { treeNodeIdsToPaths, pathsToTreeNodeIds, deduplicatePaths } from './pathConverter';
import { pathsToJson } from './pathsToJson';

function normalizeAggregatedInsights(aggregatedInsights: unknown): Record<string, any[]> {
  if (!aggregatedInsights || typeof aggregatedInsights !== 'object') return {};
  return Object.entries(aggregatedInsights as Record<string, unknown>).reduce<Record<string, any[]>>((acc, [category, items]) => {
    if (Array.isArray(items) && items.length > 0) {
      acc[category] = items;
    }
    return acc;
  }, {});
}

function aggregateCategoryPackages(categoryPackages: InsightsCategoryPackageDoc[]): Record<string, any[]> {
  return categoryPackages.reduce<Record<string, any[]>>((acc, packageDoc) => {
    const category = packageDoc.category ?? packageDoc.id;
    const scopedInsights = packageDoc.aggregatedInsights?.[category];
    const flatInsights = Array.isArray((packageDoc as any).insights) ? (packageDoc as any).insights : null;
    const insights = Array.isArray(scopedInsights) ? scopedInsights : flatInsights;
    if (Array.isArray(insights) && insights.length > 0) {
      acc[category] = insights;
    }
    return acc;
  }, {});
}

async function hydrateAnalysisForPicker(
  service: InsightsFirestoreService,
  doc: InsightsAnalysisDoc,
): Promise<InsightsAnalysisForPicker | null> {
  const parentInsights = normalizeAggregatedInsights(doc.aggregatedInsights);
  if (countTotalInsights({ ...doc, aggregatedInsights: parentInsights } as InsightsAnalysisForPicker) > 0) {
    return { ...doc, aggregatedInsights: parentInsights } as InsightsAnalysisForPicker;
  }

  const packageCollection = doc.categoryPackageCollection ?? 'category_packages';
  const categoryPackages = await service.listCategoryPackages(doc.id, packageCollection).catch((error) => {
    console.warn(`[InsightsFieldSelector] Failed to load category packages for analysis ${doc.id}:`, error);
    return [];
  });
  const scopedInsights = aggregateCategoryPackages(categoryPackages);
  if (countTotalInsights({ ...doc, aggregatedInsights: scopedInsights } as InsightsAnalysisForPicker) === 0) {
    return null;
  }

  return {
    ...doc,
    aggregatedInsights: scopedInsights,
    categoryPackages: doc.categoryPackages ?? Object.fromEntries(categoryPackages.map((pkg) => [pkg.id, pkg])),
  } as InsightsAnalysisForPicker;
}

/**
 * InsightsFieldSelector Component
 */
export const InsightsFieldSelector: React.FC<InsightsFieldSelectorProps> = ({
  projectId,
  value,
  onChange,
  height = 400,
  selectAll = false,
  taskFilter,
  onHydratedDataChange,
  onPinnedAnalysisChange,
}) => {
  const [analyses, setAnalyses] = useState<InsightsAnalysisForPicker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load completed analyses from Firestore
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new InsightsFirestoreService(projectId);
        const allDocs = await service.list({ limit: null });

        // Filter to completed analyses only. New analyses store heavy insight
        // artifacts in category_packages, so hydrate those documents before
        // deciding whether the picker can display them.
        let completed = (await Promise.all(
          allDocs
            .filter((doc: InsightsAnalysisDoc) => doc.status === 'completed')
            .map((doc) => hydrateAnalysisForPicker(service, doc)),
        )).filter((doc): doc is InsightsAnalysisForPicker => Boolean(doc));

        // Optional task filter (via selectedActorRefs → audience association)
        // Currently a simple pass-through — full task filtering requires audience loading
        // and will be simplified when taskIds field is added to InsightsAnalysis (Phase 8)
        if (taskFilter) {
          // TODO: When taskIds field exists on InsightsAnalysis, filter by it directly
          // For now, show all completed analyses (task filtering is optional)
        }

        if (isMounted) {
          setAnalyses(completed);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load insights analyses');
          console.error('Failed to load insights analyses:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [projectId, taskFilter]);

  // Count total unique insights across all analyses
  const totalInsights = useMemo(() => {
    return analyses.reduce((sum, a) => {
      if (!a.aggregatedInsights) return sum;
      return sum + Object.values(a.aggregatedInsights).reduce(
        (catSum, items) => {
          if (!Array.isArray(items)) return catSum;
          const uniqueIds = new Set(items.map((item) => item.id));
          return catSum + uniqueIds.size;
        },
        0,
      );
    }, 0);
  }, [analyses]);

  // Handle selectAll: auto-select all items after loading
  useEffect(() => {
    if (selectAll && analyses.length > 0 && value.length === 0 && !loading) {
      // Select all analyses at analysis level (path optimization will handle it)
      const allPaths = analyses.map((a) => `@insight[${a.id}]`);
      onChange(allPaths);
    }
  }, [selectAll, analyses.length, value.length, loading, onChange, analyses]);

  // Generate hydrated data when value or analyses changes.
  // W13 (IS1): `available` rides along — the UNFILTERED universe the consumer
  // needs to tell «two theses of twelve» from «two theses of two». `analyses`
  // itself stays exactly what it was: the ticked subset.
  useEffect(() => {
    if (onHydratedDataChange && analyses.length > 0) {
      const available = analyses.map((analysis) => ({
        id: analysis.id,
        insights: Object.entries(analysis.aggregatedInsights ?? {}).flatMap(([category, items]) => (
          Array.isArray(items)
            ? items
              .map((item) => ({ id: String(item?.id ?? ''), category: String(item?.category ?? category) }))
              .filter((insight) => insight.id)
            : []
        )),
      }));
      if (value.length > 0) {
        onHydratedDataChange({ ...pathsToJson(value, analyses), available });
      } else {
        onHydratedDataChange({ analyses: [], available });
      }
    }
  }, [value, analyses, onHydratedDataChange]);

  // Phase F: emit the pinned analysis (the single selected one) + the task ids it spans, so the
  // Tasks/Variables pickers can constrain to it. perTaskPackages keys → fallback sources[].taskId.
  useEffect(() => {
    if (!onPinnedAnalysisChange) return;
    let analysisId: string | null = null;
    for (const p of value) {
      const m = p.match(/^@insight\[([^\]]+)\]/);
      if (m) { analysisId = m[1]; break; }
    }
    if (!analysisId) { onPinnedAnalysisChange({ analysisId: null, taskIds: [] }); return; }
    const a = analyses.find((x) => x.id === analysisId) as any;
    let taskIds: string[] = [];
    if (a) {
      if (a.perTaskPackages && typeof a.perTaskPackages === 'object') {
        taskIds = Object.keys(a.perTaskPackages);
      }
      if (taskIds.length === 0 && a.aggregatedInsights) {
        const set = new Set<string>();
        for (const items of Object.values<any>(a.aggregatedInsights)) {
          if (Array.isArray(items)) for (const it of items) for (const s of (it?.sources || [])) if (s?.taskId) set.add(s.taskId);
        }
        taskIds = Array.from(set);
      }
    }
    onPinnedAnalysisChange({ analysisId, taskIds });
  }, [value, analyses, onPinnedAnalysisChange]);

  // Convert analyses to tree data
  const treeData = useMemo<InsightsTreeNode[]>(() => {
    return insightsToTreeNodes(analyses);
  }, [analyses]);

  // Convert value (paths) to checkedKeys (node keys)
  const checkedKeys = useMemo<Key[]>(() => {
    if (!value || value.length === 0) return [];
    return pathsToTreeNodeIds(value, treeData);
  }, [value, treeData]);

  // Handle selection changes
  const handleCheck = (checkedKeysValue: Key[] | { checked: Key[]; halfChecked: Key[] }) => {
    const checkedArray = Array.isArray(checkedKeysValue)
      ? checkedKeysValue
      : checkedKeysValue.checked;

    const stringKeys = checkedArray.map((key) => String(key));

    // Convert node keys back to optimized paths
    const paths = treeNodeIdsToPaths(stringKeys, treeData);
    const dedupedPaths = deduplicatePaths(paths);

    onChange(dedupedPaths);
  };

  // Loading state
  if (loading) {
    return (
      <Box
        data-testid="insights-picker-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 2,
          px: 4,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading insights analyses...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        data-testid="insights-picker-error"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography variant="body1" color="error">
          Error loading insights
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (analyses.length === 0) {
    return (
      <Box
        data-testid="insights-picker-empty"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No completed insights analyses found. Create one from the Insights tab.
        </Typography>
      </Box>
    );
  }

  // Display tree
  return (
    <Box
      data-testid="insights-picker-tree"
      sx={{
        height,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Tree
        treeData={treeData}
        checkable
        checkedKeys={checkedKeys}
        onCheck={handleCheck}
        defaultExpandAll={true}
        showIcon={false}
      />
    </Box>
  );
};
